'use client';

import React from 'react';
import { useTheme } from 'styled-components';

export type StopVariant = 'pickup' | 'dropoff' | 'break' | 'depot' | 'deadhead';
export type StopStatus = 'active' | 'done' | 'upcoming' | 'skipped' | 'noShow';

export interface MapStopPinProps extends React.SVGAttributes<SVGSVGElement> {
  /** Activity type — drives ring and fill colour. */
  variant?: StopVariant;
  /** Override colour with a status semantic. */
  status?: StopStatus;
  /** Pin outer diameter in px. Default 28. */
  size?: number;
}

const VARIANT_LABELS: Record<StopVariant, string> = {
  pickup: 'pickup stop',
  dropoff: 'drop-off stop',
  break: 'break stop',
  depot: 'depot stop',
  deadhead: 'deadhead waypoint',
};

/**
 * SVG map marker for a transit stop. Colour-coded by activity `variant` or
 * `status`. Designed to be absolutely-positioned over a map canvas.
 *
 * @deprecated Prefer the consolidated marker: a `DiscMarker` with `tail`
 * (generic) or `StopPin` from `@liftango/ui-ops` (typed). Those carry the
 * solid bold marker tone, a white icon, optional sequence/failure badges, and
 * `markerAnchorOffset` for precise map placement. This pale, icon-less SVG pin
 * is kept only for backwards compatibility and will be removed in a future
 * major.
 *
 * @example
 * <div style={{ position: 'relative' }}>
 *   <MapStopPin variant="pickup" style={{ position: 'absolute', top: 40, left: 80 }} />
 * </div>
 */
export function MapStopPin({
  variant = 'pickup',
  status,
  size = 28,
  'aria-label': ariaLabel,
  ...rest
}: MapStopPinProps) {
  const theme = useTheme();

  const activityColors = theme.colors.activity[variant];
  const fill = activityColors.fill;
  const ring = activityColors.bar;

  // Status overrides ring colour only when provided
  const ringColor = status ? theme.colors.status[status].dot : ring;

  const r = size / 2;
  const innerR = r * 0.55;
  const pointH = r * 0.6;
  const totalH = size + pointH;
  const cx = r;
  const cy = r;

  const label = ariaLabel ?? VARIANT_LABELS[variant];

  return (
    <svg
      role="img"
      aria-label={label}
      width={size}
      height={totalH}
      viewBox={`0 0 ${size} ${totalH}`}
      overflow="visible"
      {...rest}
    >
      {/* Circle body */}
      <circle cx={cx} cy={cy} r={r} fill={fill} />
      <circle cx={cx} cy={cy} r={r - 1.5} fill="none" stroke={ringColor} strokeWidth={2.5} />
      {/* Inner dot */}
      <circle cx={cx} cy={cy} r={innerR * 0.5} fill={ringColor} />
      {/* Pointer tip */}
      <polygon
        points={`${cx - r * 0.25},${cy + r * 0.7} ${cx + r * 0.25},${cy + r * 0.7} ${cx},${totalH}`}
        fill={ringColor}
      />
    </svg>
  );
}
